import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Separator } from '../ui/separator';
import {
  CreditCard,
  Download,
  Shield,
  Lock,
  FileText,
  Trash2,
  ExternalLink,
  Loader2,
  CheckCircle,
  Building2,
  Star,
  Plus,
  ChevronRight,
  LogOut
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import DeleteAccountModal from '../modals/DeleteAccountModal';
import { useAuth } from '../../context/CompleteAuthContext';

import { API_URL } from '../../config/api';

const BACKEND_URL_PM = API_URL;

const SavedPaymentMethods = () => {
  const navigate = useNavigate();
  const [methods, setMethods] = useState([]);
  const [defaultPm, setDefaultPm] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPMs = async () => {
      try {
        const res = await fetch(`${BACKEND_URL_PM}/api/payment-methods`, { credentials: 'include' });
        if (res.ok) { const d = await res.json(); setMethods(d.payment_methods || []); setDefaultPm(d.default_payment_method); }
      } catch (err) { console.error("PM fetch error:", err); }
      setLoading(false);
    };
    fetchPMs();
  }, []);

  const handleRemove = async (pmId) => {
    try {
      const res = await fetch(`${BACKEND_URL_PM}/api/payment-methods/remove`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_method_id: pmId }),
      });
      if (res.ok) setMethods(prev => prev.filter(m => m.id !== pmId));
    } catch (err) { console.error("PM remove error:", err); }
  };

  const handleSetDefault = async (pmId) => {
    try {
      const res = await fetch(`${BACKEND_URL_PM}/api/payment-methods/default`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_method_id: pmId }),
      });
      if (res.ok) setDefaultPm(pmId);
    } catch (err) { console.error("PM default error:", err); }
  };

  const achMethods = methods.filter(m => m.type === 'us_bank_account');
  const cardMethods = methods.filter(m => m.type === 'card');

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <Building2 className="w-5 h-5 text-green-600" />
            <span>Bank Accounts</span>
          </CardTitle>
          <Button size="sm" variant="outline" onClick={() => navigate('/bank-accounts')} data-testid="billing-manage-pms-btn">
            Manage <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-gray-300" /></div>
        ) : methods.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-sm text-gray-400 mb-3">No bank accounts linked</p>
            <Button size="sm" variant="outline" onClick={() => navigate('/bank-accounts')} data-testid="billing-add-pm-btn">
              <Plus className="w-4 h-4 mr-1" /> Add bank
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {achMethods.map(pm => (
              <div key={pm.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center"><Building2 className="w-4 h-4 text-green-600" /></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{pm.bank_name}</p>
                    <p className="text-xs text-gray-400">····{pm.last4} · {pm.account_type}</p>
                  </div>
                  {pm.id === defaultPm && <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs ml-1">Default</Badge>}
                </div>
                <div className="flex items-center space-x-1">
                  {pm.id !== defaultPm && <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-amber-600" onClick={() => handleSetDefault(pm.id)}><Star className="w-3.5 h-3.5" /></Button>}
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-red-600" onClick={() => handleRemove(pm.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            ))}
            {cardMethods.map(pm => (
              <div key={pm.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center"><CreditCard className="w-4 h-4 text-blue-600" /></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 capitalize">{pm.brand}</p>
                    <p className="text-xs text-gray-400">····{pm.last4} · {pm.exp_month}/{pm.exp_year}</p>
                  </div>
                </div>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-red-600" onClick={() => handleRemove(pm.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const VenmoUsernameCard = () => {
  const [value, setValue] = useState('');
  const [saved, setSaved] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load existing handle from server on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL_PM}/api/settings/profile`, { credentials: 'include' });
        if (!res.ok) return;
        const d = await res.json();
        if (cancelled) return;
        const current = d.venmo_username || '';
        setSaved(current);
        setValue(current);
      } catch (err) {
        console.error('Venmo profile load error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Strip a leading @ client-side for nicer error messages; server normalizes too.
      const payload = value.trim().replace(/^@/, '');
      const res = await fetch(`${BACKEND_URL_PM}/api/settings/profile`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ venmo_username: payload }),
      });
      if (res.ok) {
        setSaved(payload);
        setValue(payload);
        toast.success(payload ? 'Venmo username saved' : 'Venmo username removed');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save Venmo username');
      }
    } catch (err) {
      console.error('Venmo save error:', err);
      toast.error('Connection error');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setValue('');
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL_PM}/api/settings/profile`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ venmo_username: '' }),
      });
      if (res.ok) {
        setSaved('');
        toast.success('Venmo username removed');
      } else {
        toast.error('Could not remove Venmo username');
      }
    } catch (err) {
      console.error('Venmo clear error:', err);
    } finally {
      setSaving(false);
    }
  };

  const dirty = value.trim().replace(/^@/, '') !== saved;

  return (
    <Card data-testid="settings-venmo-card">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <span className="w-5 h-5 inline-flex items-center justify-center rounded bg-sky-100 text-sky-600 font-bold text-xs">V</span>
          <span>Venmo username</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-gray-600 mb-3 leading-relaxed">
          Let guests pay you through Venmo.
        </p>
        <Label htmlFor="venmo_username">Username</Label>
        <div className="flex gap-2 mt-1">
          <Input
            id="venmo_username"
            data-testid="settings-venmo-input"
            placeholder="@yourhandle"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={loading || saving}
            className="flex-1"
          />
          <Button
            onClick={handleSave}
            disabled={loading || saving || !dirty}
            data-testid="settings-venmo-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
          </Button>
        </div>
        {saved && (
          <button
            type="button"
            onClick={handleClear}
            disabled={saving}
            className="text-xs text-gray-400 hover:text-red-600 mt-2"
            data-testid="settings-venmo-clear"
          >
            Remove Venmo username
          </button>
        )}
        <p className="text-xs text-gray-400 mt-3">
          1–30 characters. Letters, numbers, underscore, or hyphen.
        </p>
      </CardContent>
    </Card>
  );
};

const BillingTab = ({ user }) => {
  const { logout } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportPassword, setExportPassword] = useState('');
  const [exportSections, setExportSections] = useState({
    transactions: true,
    groups: true,
    settings: true
  });

  const BACKEND_URL = API_URL;

  const handleBillingPortal = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/settings/billing/portal`,
        { withCredentials: true }
      );

      if (response.data.portal_url) {
        window.open(response.data.portal_url, '_blank');
        toast.success('Opening Stripe billing portal...');
      } else {
        toast.info('Billing portal not available');
      }
    } catch (error) {
      console.error('Billing portal error:', error);
      toast.error('Unable to access billing portal');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDataExport = async () => {
    setIsExporting(true);
    toast.loading('Preparing your data export...', { id: 'export-toast' });
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/settings/data/export`,
        {
          password: exportPassword || null,
          include_transactions: exportSections.transactions,
          include_groups: exportSections.groups,
          include_settings: exportSections.settings
        },
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const filename = exportPassword 
        ? `payo_data_${user.id}.zip` 
        : `payo_data_${user.id}.json`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('✓ Data export ready! Download started.', { 
        id: 'export-toast',
        duration: 3000,
        position: 'top-center'
      });
      setExportPassword('');
    } catch (error) {
      console.error('Data export error:', error);
      toast.error('Data export failed', { id: 'export-toast' });
    } finally {
      setIsExporting(false);
    }
  };

 

  const handleSignOut = async () => {
    setIsLoading(true);
    try {
      toast.success('Signing out...');
      // Use the logout function from CompleteAuthContext
      // This will call the backend, clear cookies, and redirect to /login?loggedOut=1
      await logout();
    } catch (error) {
      console.error('Sign out error:', error);
      toast.error('Sign out failed');
      setIsLoading(false);
    }
    // Note: setIsLoading(false) not needed in finally block
    // because logout() redirects the page
  };

  return (
    <div className="space-y-5">
 

      {/* Payment Security Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Shield className="w-5 h-5 text-green-600" />
            <span>Payment Security</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
            <div className="text-sm text-green-800">
              <p className="font-medium">Powered by Stripe</p>
              <p className="text-green-700 mt-0.5">PCI-compliant processing. Payo never stores your card details.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Methods — Real saved methods */}
      <SavedPaymentMethods />

      {/* Venmo username — purely client-side prefill (no API integration) */}
      <VenmoUsernameCard />

      {/* Account Controls */}
      <Card className="border-gray-200">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 text-gray-900">
            <Shield className="w-5 h-5 text-gray-600" />
            <span>Account Controls</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Sign Out */}
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div>
                <p className="font-medium text-gray-900">Sign Out</p>
                <p className="text-sm text-gray-600">
                  Sign out of your account on this device
                </p>
              </div>
              <Button
                variant="outline"
                onClick={handleSignOut}
                disabled={isLoading}
                className="text-gray-700 border-gray-300 hover:bg-gray-100"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>

            {/* Account Deletion */}
            <Separator />
            
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div>
                <p className="font-medium text-gray-900">Delete Account</p>
                <p className="text-sm text-gray-600">
                  Permanently delete your account and all associated data
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowDeleteModal(true)}
                className="text-red-700 border-red-300 hover:bg-red-50 font-medium"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Account
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Account Modal */}
      <DeleteAccountModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        user={user}
      />
    </div>
  );
};

export default BillingTab;